import { useState, useEffect } from "react";
import { Loader2, GitMerge } from "lucide-react";
import { toast } from "sonner";
import apiClient from "../../services/api";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";

const FIELDS = [
  ["first_name", "First name"], ["last_name", "Last name"], ["company", "Company"], ["title", "Title"], ["email", "Email"],
  ["phone_office", "Office phone"], ["phone_mobile", "Mobile phone"], ["street", "Street"], ["apt", "Apt / Suite"],
  ["city", "City"], ["state", "State"], ["zip_code", "ZIP"], ["notes", "Notes"],
];

// Pre-pick: the non-empty side; if both filled, the more recently updated contact wins.
const initialPicks = (a, b) => {
  const newer = (a.updated_at || "") >= (b.updated_at || "") ? "a" : "b";
  return Object.fromEntries(FIELDS.map(([k]) => {
    const av = (a[k] || "").trim(), bv = (b[k] || "").trim();
    if (av && !bv) return [k, "a"];
    if (bv && !av) return [k, "b"];
    return [k, av && bv ? newer : null];
  }));
};

const MergeContactsDialog = ({ open, onOpenChange, pair, onMerged }) => {
  const [picks, setPicks] = useState({});
  const [saving, setSaving] = useState(false);
  const [a, b] = pair || [];

  useEffect(() => { if (open && a && b) setPicks(initialPicks(a, b)); }, [open, a, b]);
  if (!a || !b) return null;

  const merged = Object.fromEntries(FIELDS.map(([k]) => [k, picks[k] ? (picks[k] === "b" ? b : a)[k] || "" : ""]));
  const keep = (a.updated_at || "") >= (b.updated_at || "") ? a : b;
  const remove = keep === a ? b : a;

  const submit = async () => {
    setSaving(true);
    try {
      const r = (await apiClient.post("/shared-contacts/merge", { keep_id: keep.id, remove_id: remove.id, fields: merged })).data;
      toast.success(`Merged into ${r.contact.display_name}${r.removed_from_exchange ? "" : " — Outlook removal will retry at next sync"}`);
      onMerged?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Merge failed");
    } finally { setSaving(false); }
  };

  const Cell = ({ side, k }) => {
    const c = side === "a" ? a : b;
    const v = (c[k] || "").trim();
    const on = picks[k] === side;
    return (
      <button type="button" onClick={() => v && setPicks((p) => ({ ...p, [k]: side }))} disabled={!v}
        className={`text-left text-sm rounded-md border px-2 py-1.5 min-h-[34px] w-full transition-colors ${on ? "border-sky-500 bg-sky-50 text-sky-900 font-medium" : v ? "hover:bg-accent" : "text-muted-foreground/50 border-dashed"}`}
        data-testid={`merge-${k}-${side}`}>
        {v || "—"}
      </button>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="merge-contacts-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><GitMerge className="w-5 h-5" /> Merge contacts</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Click a value to choose it. The highlighted side is kept for each field. The result is saved to <strong>{keep.display_name}</strong>; <strong>{remove.display_name}</strong> is deleted (and removed from Outlook).</p>
        <div className="grid grid-cols-[110px_1fr_1fr] gap-2 items-center text-xs">
          <div />
          <div className="font-semibold truncate" data-testid="merge-side-a">{a.display_name}</div>
          <div className="font-semibold truncate" data-testid="merge-side-b">{b.display_name}</div>
          {FIELDS.map(([k, label]) => (
            <div key={k} className="contents">
              <div className="text-muted-foreground">{label}</div>
              <Cell side="a" k={k} />
              <Cell side="b" k={k} />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="merge-confirm-btn">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Merge into {keep.display_name}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MergeContactsDialog;
